import React, { useState, useEffect } from 'react';
import { Mail, Send } from 'lucide-react';
import { api } from '../services/api';
import PublicPageLayout from '../components/PublicPageLayout';

const HelpPage: React.FC = () => {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoonnummer: '',
    bericht: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Help Center - Solofix';
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.naam.trim()) {
      setError('Naam is verplicht');
      return;
    }
    if (!formData.email.trim()) {
      setError('E-mailadres is verplicht');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Voer een geldig e-mailadres in');
      return;
    }
    if (!formData.bericht.trim()) {
      setError('Bericht is verplicht');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/contact', formData);
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <PublicPageLayout 
        title="Bericht Verzonden!" 
        showBackButton={false}
      >
        <div className="max-w-2xl mx-auto text-center">
          <div
            className="bg-neon-lime/[.12] rounded-full p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center"
            style={{ boxShadow: '0 0 30px rgba(184,255,61,.35)' }}
          >
            <Mail className="w-8 h-8 text-neon-lime" />
          </div>

          <h2 className="text-2xl font-bold text-fg-bright mb-4">Bedankt voor je bericht!</h2>
          <p className="text-fg-body mb-8">
            We hebben je bericht ontvangen en zullen zo spoedig mogelijk reageren.
            Je ontvangt binnen 1-2 werkdagen een reactie op het opgegeven e-mailadres.
          </p>

          <div className="space-y-4">
            <a
              href="/"
              className="sf-btn-primary px-6 py-3"
            >
              Terug naar Solofix
            </a>
          </div>
        </div>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout 
      title="Help Center" 
      subtitle="Heb je vragen of hulp nodig? We helpen je graag!"
    >
      <div className="max-w-2xl mx-auto">
        {/* Contact Info */}
        <div className="sf-item p-6 mb-8">
          <h2 className="text-xl font-bold text-fg-bright mb-4">Contact Informatie</h2>
          <div className="space-y-2">
            <p className="text-fg-body">
              <strong>Email:</strong> <a href="mailto:info@solofix.nl" className="sf-link">info@solofix.nl</a>
            </p>
            <p className="text-fg-body">
              <strong>Reactietijd:</strong> Binnen 1-2 werkdagen
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="sf-item p-6">
          <h2 className="text-xl font-bold text-fg-bright mb-6">Stuur ons een bericht</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-field border border-neon-pink/30 bg-neon-pink/[.08] px-4 py-3 text-sm text-neon-pink-soft">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="naam" className="block text-sm font-semibold text-fg-soft mb-2">
                Naam *
              </label>
              <input
                type="text"
                id="naam"
                name="naam"
                value={formData.naam}
                onChange={handleInputChange}
                required
                className="sf-input"
                placeholder="Je naam"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-fg-soft mb-2">
                E-mailadres *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="sf-input"
                placeholder="je@email.nl"
              />
            </div>

            <div>
              <label htmlFor="telefoonnummer" className="block text-sm font-semibold text-fg-soft mb-2">
                Telefoonnummer
              </label>
              <input
                type="tel"
                id="telefoonnummer"
                name="telefoonnummer"
                value={formData.telefoonnummer}
                onChange={handleInputChange}
                className="sf-input"
                placeholder="06-12345678 (optioneel)"
              />
            </div>

            <div>
              <label htmlFor="bericht" className="block text-sm font-semibold text-fg-soft mb-2">
                Bericht *
              </label>
              <textarea
                id="bericht"
                name="bericht"
                value={formData.bericht}
                onChange={handleInputChange}
                required
                rows={5}
                className="sf-input"
                placeholder="Beschrijf je vraag of probleem..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="sf-btn-primary w-full py-3 px-4"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ink mr-2"></div>
                  Bezig met verzenden...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Bericht Verzenden
                </>
              )}
            </button>
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-fg-muted text-sm">
          <p>
            Voor acute problemen tijdens evenementen kun je ook direct contact opnemen via{' '}
            <a href="mailto:info@solofix.nl" className="sf-link">
              info@solofix.nl
            </a>
          </p>
        </div>
      </div>
    </PublicPageLayout>
  );
};

export default HelpPage;